import axios from 'axios';
import { history } from '../../App';
import { recentlyViewed } from '../actions/RecentViewActions';
import { setAndScroll } from './ChunkActions';
import { actionsToHideModal } from '../../redux/actions/AppHeaderModalActions';
import { checkSession } from '../../components/Security/Security';
import { getBaseUrl } from '../../utility';
import i18n from '../../i18n';
import { xApiEvent, actorInfo } from '../../services/xApiService';
import ReactGA from 'react-ga';
ReactGA.initialize('UA-54968464-213');

// UTILITY FUNCTIONS
export const navigate = item => {
  return async (dispatch, getState) => {
    const { activeChunk } = getState().ChunkState;
    const { learningPathJson } = getState().CourseState;

    // homepage handling
    if (item.li === learningPathJson['@id']) {
      history.push(`${process.env.PUBLIC_URL}/${learningPathJson['@id']}`);
      dispatch(CourseActions.stepChange(history.location));
    } else {
      let splitActiveChunk = activeChunk.split('.');
      let splitClickedItem = item.li.split('.');
      let isBlockClicked = splitClickedItem.length === 2;
      let currentBlock = splitActiveChunk.slice(0, 2);
      let isCurentBlock =
        splitClickedItem.slice(0, 2).join('.') === currentBlock.join('.');
      let navigateTo = item.li;

      if (isBlockClicked) {
        navigateTo = `${navigateTo}.1`;
      }
      dispatch(actionsToHideModal('CLOSE_MODAL'));
      // dispatch({ type: 'USER_SCROLL', userScroll: false });
      window.userScrolled = false;

      if (isCurentBlock) {
        dispatch(setAndScroll(navigateTo));
        history.push(
          `${process.env.PUBLIC_URL}/${learningPathJson['@id']}/${navigateTo}`
        );
      } else {
        const previousLI = history.location.pathname.includes('.')
          ? history.location.pathname.split('/').pop()
          : undefined;

        history.push(
          `${process.env.PUBLIC_URL}/${learningPathJson['@id']}/${navigateTo}`
        );
        dispatch(CourseActions.stepChange(history.location, previousLI));
      }
    }

    // do Google Analytics only if not offline build
    if (!process.env.REACT_APP_OFFLINE) {
      ReactGA.pageview(window.location.pathname);
    }
  };
};

export const updateLi = item => {
  return (dispatch, getState) => {
    const { courseId } = getState().CourseState;
    dispatch({
      type: 'SET_ACTIVE_CHUNK',
      activeChunk: item
    });
    // this will replace the URL without adding to the page history (back button)
    history.replace(`${process.env.PUBLIC_URL}/${courseId}/${item}`);
  };
};

const getLanguage = availableCourseLanguages => {
  // get the users detected browser language from i18n
  let lang = i18n.language;

  // get lang object in availableCourses
  const langObject = availableCourseLanguages.find(l => l.code === lang);
  // if it's available for the course, use it, otherwise fallback to english
  lang = langObject ? langObject : { code: 'en-US', prod: true };
  // TODO: create more specific rules for language fallbacks
  // For example, if we have pt-PT as an available language, but not pt-BR, a pt-BR user should get pt-PT before en-US
  return lang;
};

// takes learningPath json and outputs a model that we can work with in the UI
const applyLerningPathModel = (
  learningPathObject,
  courseId,
  language,
  dataUrlBase
) => {
  let modelItem = {
    id: learningPathObject['id'],
    url: `/${courseId}/${learningPathObject['slug']}`,
    dataUrl: `${dataUrlBase}/${courseId}${
      learningPathObject['dataUrl'] === './'
        ? ''
        : learningPathObject['dataUrl']
    }`,
    name: learningPathObject['name'],
    state: learningPathObject['state'],
    li: learningPathObject['li'],
    chunks: learningPathObject['chunks'],
    children: learningPathObject['children']
      ? learningPathObject['children'].map(chld =>
          applyLerningPathModel(chld, courseId, language)
        )
      : []
  };
  return modelItem;
};

const flattenLearningPathJson = tree => {
  let flat = [];
  let homepage = JSON.parse(JSON.stringify(tree))[0];
  delete homepage.hasPart;
  flat.push(homepage);
  tree[0].hasPart.forEach(m =>
    m.hasPart.forEach(b => {
      b.li = `${b.li}`;
      flat.push(b);
    })
  );
  return flat;
};

// Gets the current learning path step
const getCurrentStep = (flattened, currentStepId) => {
  const currentLearningPath = flattened.filter(lp => {
    return lp.li === currentStepId;
  })[0];
  return currentLearningPath;
};

// Gets the next learning path step
const getNextStep = (flattened, currentStep) => {
  const currentIndex = flattened.findIndex(path => path.li === currentStep);
  return flattened[currentIndex + 1];
};

// Gets the previous learning path step
const getPreviousStep = (flattened, currentStep) => {
  const currentIndex = flattened.findIndex(path => path.li === currentStep);
  return flattened[currentIndex - 1];
};

const getCourseStepId = location => {
  let pathTokens = location.pathname;
  let regex = new RegExp(process.env.PUBLIC_URL, 'g');
  pathTokens = pathTokens.replace(regex, '').split('/');

  // kick off the leading empty token
  pathTokens.shift();

  // the next one is the learningPathId and then stepId
  const course = pathTokens[0];
  // kick off the course
  pathTokens.shift();
  let step = pathTokens.join('/') ? pathTokens.join('/') : course;

  return {
    courseId: course,
    stepId: step
  };
};

// checks to verify we have content for what we're about to load
const validateLI = (lpJson, fullLi) => {
  // if this hits, we're on the homepage
  if (lpJson['@id'] === fullLi) return;
  // break up the LI
  let liSplit = fullLi.split('.');
  let modLi = liSplit[0];
  let blockLi = liSplit[0] + '.' + liSplit[1];
  let urlChunk = liSplit[2];
  let mod = lpJson.hasPart.find(m => m.li === modLi);
  // if we have a mod, continue
  if (mod) {
    let block = mod.hasPart.find(b => b.li === blockLi);
    // if we have a block...
    if (block) {
      // if the chunk from the URL is greater than the block's chunks
      // use the current block's chunk length in a new LI and redirect
      if (block.chunks.length < parseInt(urlChunk)) {
        let newStep = `${blockLi}.${block.chunks.length}`;
        history.push(`${process.env.PUBLIC_URL}/${lpJson['@id']}/${newStep}`);
      }
    } else {
      // no block, page not found
      history.push(`${process.env.PUBLIC_URL}/not-found`);
    }
  } else {
    // no mode, page not found
    history.push(`${process.env.PUBLIC_URL}/not-found`);
  }
};

// REDUX ACTIONS
const CourseActions = {
  initCourse: () => {
    const pathId = getCourseStepId(history.location);
    let chunk = pathId.stepId;
    let previousLI = pathId.stepId.includes('.') ? pathId.stepId : undefined;
    let dataUrlBase = getBaseUrl(pathId.courseId);
    return dispatch => {
      const availableCourseLanguagesUrl = [
        dataUrlBase,
        pathId.courseId,
        `available-languages.json`
      ]
        .join('/')
        .replace('//m', '/m');

      // first, retrieve the list of available languages for the course
      axios
        .get(availableCourseLanguagesUrl)
        .then(langs => {
          const availableCourseLanguages = langs.data;
          // then, get the users language code or appropriate fallback
          const language = getLanguage(availableCourseLanguages);
          const manifestUrl = [
            dataUrlBase,
            pathId.courseId,
            // once we have the language code, we can fetch its manifest_xx-XX.json file
            `lang/manifest_${language.code}.json`
          ]
            .join('/')
            .replace('//m', '/m');

          axios
            .get(manifestUrl)
            .then(async res => {
              // xAPI Actor Info
              let actor = await actorInfo();
              // finally, we set language and availableCourseLanguages in the store for app-wide use
              dispatch(
                CourseActions.buildLearningPaths({
                  type: 'SET_COURSE',
                  actor: actor,
                  dataUrlBase: dataUrlBase,
                  language: language,
                  availableCourseLanguages: availableCourseLanguages,
                  course: pathId.courseId,
                  stepId: pathId.stepId,
                  previousLI: previousLI,
                  learningPathJson: res.data,
                  currentLoc: history.location,
                  chunk: chunk
                })
              );
            })
            .catch(err => {
              // eslint-disable-next-line no-console
              console.log('err :', err, err.response);
              if (err.response.status === 404) {
                // TODO: custom "course not found" page
                // send them to the not-found page
                history.push(`${process.env.PUBLIC_URL}/not-found`);
              }
            });
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.log('err :', err, err.response);
          if (err.response.status === 404) {
            // TODO: custom "course not found" page
            // send them to the not-found page
            history.push(`${process.env.PUBLIC_URL}/not-found`);
          }
        });
    };
  },
  stepChange: (location, previousLI) => {
    const pathId = getCourseStepId(location);
    return (dispatch, getState) => {
      const {
        dataUrlBase,
        language,
        courseId,
        learningPathJson
      } = getState().CourseState;

      // check the LTI session on block change
      checkSession().then(valid => {
        // if we're valid, handle the step
        if (valid) {
          dispatch(
            CourseActions.buildLearningPaths({
              type: 'STEP_CHANGE',
              dataUrlBase: dataUrlBase,
              language: language,
              course: courseId,
              stepId: pathId.stepId,
              learningPathJson: learningPathJson,
              currentLoc: location,
              chunk: pathId.stepId,
              previousLI: previousLI
            })
          );
        }
      });
    };
  },
  buildLearningPaths: props => {
    return (dispatch, getState) => {
      const {
        dataUrlBase,
        course,
        language,
        stepId,
        learningPathJson,
        chunk,
        previousLI
      } = props;
      validateLI(learningPathJson, stepId);

      const {
        flatpath,
        // get current module as last module as we're about to update the current module, and need the time it was first entered
        currentModule: lastModule,
        // get current block as last block as we're about to update the current block, and need the time it was first entered
        currentBlock: lastBlock
      } = getState().CourseState;
      // setup for tracking durration
      const previousModuleLI = previousLI ? previousLI.split('.')[0] : null;
      const previousBlockLI = previousLI ? previousLI.split('.')[1] : null;

      let flattentedTree = flatpath
        ? flatpath
        : flattenLearningPathJson([learningPathJson]).map(path =>
            applyLerningPathModel(path, course, language, dataUrlBase)
          );
      let blockLi =
        // if home page
        stepId === learningPathJson['@id']
          ? stepId
          : // assume 3 digit li, get the block li
            stepId.split('.').slice(0, -1).join('.');
      // does the block exist?
      let currentStep = getCurrentStep(flattentedTree, blockLi);
      if (!currentStep) {
        if (stepId.split('.').length === 2) {
          history.push(`/${course}/${stepId}.1`);
        } else {
          history.push(`${process.env.PUBLIC_URL}/not-found`);
        }
      } else {
        let modLi = blockLi.split('.')[0];

        // xAPI stuff
        const findMod = learningPathJson.hasPart.find(m => m.li === modLi);
        let currentModule = findMod
          ? JSON.parse(JSON.stringify(findMod)) // so we don't set the learningPathJson vua currentModule later
          : undefined;

        // Module duration
        if (
          currentModule &&
          previousModuleLI &&
          previousModuleLI !== currentModule.li
        ) {
          // get duration in miliseconds
          let durrationOnLastModule = new Date() - lastModule.startTime;
          // call xAPI service
          xApiEvent('Change Module', 'Capture Module Duration', {
            moduleLi: lastModule.li,
            moduleName: lastModule.name,
            duration: durrationOnLastModule
          });
        }
        // if current mod has no start time, add it
        if (currentModule && !currentModule.startTime) {
          currentModule.startTime = new Date();
        }

        let currentBlock = currentModule
          ? JSON.parse(
              JSON.stringify(currentModule.hasPart.find(b => b.li === blockLi))
            )
          : learningPathJson; // homepage if no module

        // Block xAPI stuff
        if (
          currentBlock &&
          previousBlockLI &&
          previousBlockLI !== currentBlock.li.split('.')[1]
        ) {
          // get duration in miliseconds
          let durrationOnLastBlock = new Date() - lastBlock.startTime;
          // call xAPI service
          xApiEvent('Change Block', 'Capture Block Duration', {
            blockLi: lastBlock.li,
            blockName: lastBlock.name,
            duration: durrationOnLastBlock
          });
        }

        if (currentBlock && !currentBlock.startTime) {
          currentBlock.startTime = new Date();
        }
        // xAPI fin

        let nextStep = currentBlock
          ? getNextStep(flattentedTree, currentBlock.li)
          : flattentedTree[0];
        let previousStep = currentBlock
          ? getPreviousStep(flattentedTree, currentBlock.li)
          : undefined;

        dispatch({ type: 'UPDATE_DATA_URL', dataUrl: currentStep.dataUrl });
        dispatch({
          ...props,
          courseTitle: learningPathJson.name,
          course,
          currentStep,
          nextStep,
          previousStep,
          flattentedTree,
          currentModule: currentModule,
          currentBlock: currentBlock
        });

        dispatch({
          type: 'SET_ACTIVE_CHUNK',
          activeChunk: chunk
        });
        dispatch(recentlyViewed(chunk));
      }
    };
  }
};

export default CourseActions;
